import { useEffect, useState } from 'react'
import { RefreshCw, Hash, ListChecks, PlugZap, Download, Clock } from 'lucide-react'
import api from '../services/api'
import { Linha } from './LancamentoDepositos'

interface Parametros { empcod: number; url: string; ultimoId: number; ultimaBusca: string | null; origemEstoque: string; postoLub: string; tipesc: string }
interface Item {
  id: string; data: string; hora: string; litros: number; odometro: number; custo: number; placa: string; frota: string
  combustivel: string; motorista: string; frentista: string; letra: string; veicod: string | null; deposito: string | null
  valorPrevisto: number | null; situacao: 'novo' | 'oficina' | 'ja_importado' | 'duplicado' | 'erro' | 'lubrificante'; erros: string[]
  erp?: { deposito: string; letra: string; litros: number; odometro: number; valor: number; veicod: string; ordnum?: number }
  confere?: boolean
  gravado?: boolean
  ordnum?: number
  incorreto?: number
}

interface Agendamento {
  ativo: boolean; minutos: number; ultima: string | null; proxima: string | null; liberado: boolean; executando: boolean
  resultado: { gravados: number; correcao: number; outros: number; erro?: string } | null
}
const dataHora = (iso: string | null) => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'

const fmtN = (v: number | null | undefined, c: number) => v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const br = (d: string) => d ? d.split('-').reverse().join('/') : ''
const SIT: Record<Item['situacao'], { t: string; bg: string; fg: string }> = {
  novo: { t: 'Novo', bg: '#dbeafe', fg: '#1d4ed8' },
  oficina: { t: 'Oficina (só estoque)', bg: '#e0f2fe', fg: '#0369a1' },
  ja_importado: { t: 'Já no ERP', bg: '#f1f5f9', fg: '#475569' },
  duplicado: { t: 'Duplicado (já lançado)', bg: '#ede9fe', fg: '#6d28d9' },
  erro: { t: 'Erro', bg: '#fee2e2', fg: '#b91c1c' },
  lubrificante: { t: 'Lubrificante → OS', bg: '#fef3c7', fg: '#b45309' },
}

// Integração CTA Plus (GeneXus PCtaPlus) — etapa 1: busca no CTA e mostra o que seria gravado, sem gravar.
export default function IntegracaoCtaPlus() {
  const [par, setPar] = useState<Parametros | null>(null)
  const [desde, setDesde] = useState('')
  const [max, setMax] = useState('50')
  const [itens, setItens] = useState<Item[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState('')
  const [importando, setImportando] = useState(false)
  const [ag, setAg] = useState<Agendamento | null>(null)
  const [agMin, setAgMin] = useState('30')
  const [agMsg, setAgMsg] = useState('')
  const [aviso, setAviso] = useState('')

  useEffect(() => {
    api.get('/combustivel/cta/parametros').then(r => { setPar(r.data); setDesde(String(r.data.ultimoId)) })
      .catch(e => setErro(e?.response?.data?.error || 'Erro ao ler parâmetros do CTA Plus.'))
    carregarAg()
    const t = setInterval(carregarAg, 60000)
    return () => clearInterval(t)
  }, [])

  async function carregarAg() {
    try { const r = (await api.get('/combustivel/cta/agendamento')).data; setAg(r); setAgMin(m => (m === '30' || !m ? String(r.minutos) : m)) } catch { /* sem agendamento */ }
  }

  async function salvarAg(ativo: boolean) {
    setAgMsg('')
    try {
      const r = (await api.put('/combustivel/cta/agendamento', { ativo, minutos: Number(agMin) })).data
      setAg(r); setAgMin(String(r.minutos))
      setAgMsg(ativo ? `Importação automática ligada: a cada ${r.minutos} minutos.` : 'Importação automática desligada.')
    } catch (e: any) { setAgMsg(e?.response?.data?.error || 'Erro ao salvar.') }
  }

  async function simular() {
    setErro(''); setCarregando(true)
    try { setItens((await api.get('/combustivel/cta/simular', { params: { desde, max } })).data.itens) }
    catch (e: any) { setItens(null); setErro(e?.response?.data?.error || 'Erro ao consultar o CTA Plus.') }
    finally { setCarregando(false) }
  }

  // Grava de verdade a partir do último ID importado (só onde CTA_IMPORTACAO=on no servidor).
  async function importar() {
    if (!window.confirm(`Importar até ${max} abastecimento(s) do CTA a partir do último ID (${par?.ultimoId})? Isso grava abastecimentos, baixa estoque e atualiza hodômetros.`)) return
    setErro(''); setAviso(''); setImportando(true)
    try {
      const r = (await api.post('/combustivel/cta/importar', { max })).data
      setItens(r.itens)
      const gravados = r.itens.filter((i: Item) => i.gravado).length
      setAviso(`${gravados} gravado(s). Último ID agora: ${r.ultimoId}.`)
      setPar(p => p ? { ...p, ultimoId: r.ultimoId } : p)
      setDesde(String(r.ultimoId))
    } catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao importar do CTA Plus.') }
    finally { setImportando(false) }
  }

  const th: React.CSSProperties = { padding: '0.4rem 0.45rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0 }
  const td: React.CSSProperties = { padding: '0.28rem 0.45rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
  const dir: React.CSSProperties = { ...td, textAlign: 'right' }
  const cont = (s: Item['situacao']) => itens?.filter(i => i.situacao === s).length ?? 0
  const divergentes = itens?.filter(i => i.situacao === 'ja_importado' && i.confere === false).length ?? 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <PlugZap size={18} color="#fff" />
            </span>
            <div>
              <h2>Integração CTA Plus</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Modo de teste — busca no CTA e mostra o que seria gravado, sem gravar nada</div>
            </div>
          </div>
        </div>
        <div className="modal-corpo">
          {par && (
            <div style={{ fontSize: '0.78rem', color: '#475569', marginBottom: '0.8rem', lineHeight: 1.6 }}>
              Servidor: <b>{par.url}</b> • Último ID importado (ERP): <b>{par.ultimoId}</b> • Última busca: <b>{par.ultimaBusca || '—'}</b>
            </div>
          )}
          <Linha label="A partir do ID (teste)" icon={Hash}>
            <input value={desde} onChange={e => setDesde(e.target.value.replace(/\D/g, ''))} maxLength={10} style={{ maxWidth: 140 }} />
          </Linha>
          <Linha label="Máx. registros" icon={ListChecks}>
            <input value={max} onChange={e => setMax(e.target.value.replace(/\D/g, ''))} maxLength={3} style={{ maxWidth: 80 }} />
          </Linha>
          {aviso && <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>{aviso}</div>}
          {erro && <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={simular} disabled={carregando || !par} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <RefreshCw size={15} /> {carregando ? 'Consultando CTA...' : 'Testar (sem gravar)'}
          </button>
          <button className="btn" onClick={importar} disabled={importando || carregando || !par} style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            <Download size={15} /> {importando ? 'Importando...' : 'Importar agora'}
          </button>
        </div>
      </div>

      {ag && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 800, color: '#1b3f7b', marginBottom: '0.6rem' }}>
              <Clock size={16} /> Importação automática
              <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', background: ag.ativo ? '#dcfce7' : '#f1f5f9', color: ag.ativo ? '#15803d' : '#475569' }}>
                {ag.executando ? 'importando agora…' : ag.ativo ? 'ligada' : 'desligada'}
              </span>
            </div>
            {!ag.liberado && <div style={{ fontSize: '0.78rem', color: '#b91c1c', marginBottom: '0.5rem' }}>Importação não liberada nesta base (CTA_IMPORTACAO) — a rotina não roda aqui.</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem', flexWrap: 'wrap' }}>
              A cada <input value={agMin} onChange={e => setAgMin(e.target.value.replace(/\D/g, ''))} maxLength={4} style={{ width: 70 }} /> minutos
              {ag.ativo
                ? (<>
                    <button className="btn btn-primary btn-sm" onClick={() => salvarAg(true)}>Salvar intervalo</button>
                    <button className="btn btn-sm" style={{ background: '#b91c1c', color: '#fff' }} onClick={() => salvarAg(false)}>Desligar</button>
                  </>)
                : <button className="btn btn-sm" style={{ background: '#15803d', color: '#fff' }} onClick={() => salvarAg(true)}>Ligar</button>}
            </div>
            <div style={{ fontSize: '0.76rem', color: '#475569', marginTop: '0.6rem', lineHeight: 1.7 }}>
              Última execução: <b>{dataHora(ag.ultima)}</b>{ag.ativo && <> • Próxima: <b>{dataHora(ag.proxima)}</b></>}
              {ag.resultado && (ag.resultado.erro
                ? <div style={{ color: '#b91c1c' }}>Última execução com erro: {ag.resultado.erro}</div>
                : <div>Resultado: <b style={{ color: '#15803d' }}>{ag.resultado.gravados} gravado(s)</b> • <b style={{ color: '#b45309' }}>{ag.resultado.correcao} pra correção</b> • {ag.resultado.outros} já importado(s)/outros</div>)}
            </div>
            {agMsg && <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', fontWeight: 600, color: '#1d4ed8' }}>{agMsg}</div>}
          </div>
        </div>
      )}

      {itens && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#475569', marginBottom: '0.7rem' }}>
              <span><b>{itens.length}</b> registro(s)</span>
              <span>Novos: <b style={{ color: '#1d4ed8' }}>{cont('novo')}</b></span>
              <span>Já no ERP: <b>{cont('ja_importado')}</b>{divergentes > 0 && <b style={{ color: '#b91c1c' }}> ({divergentes} com diferença)</b>}</span>
              <span>Duplicados: <b style={{ color: '#6d28d9' }}>{cont('duplicado')}</b></span>
              <span>Erros: <b style={{ color: '#b91c1c' }}>{cont('erro')}</b></span>
              <span>Lubrificante: <b>{cont('lubrificante')}</b></span>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>{['ID CTA', 'Data', 'Hora', 'Placa', 'Veículo', 'Comb.', 'Litros', 'Hodômetro', 'Depósito', 'Valor previsto', 'Situação', 'Gravado pelo ERP / Erros'].map(c =>
                    <th key={c} style={{ ...th, textAlign: ['Litros', 'Hodômetro', 'Valor previsto'].includes(c) ? 'right' : 'left' }}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {itens.map((i, k) => {
                    const s = SIT[i.situacao]
                    return (
                      <tr key={i.id} style={{ background: k % 2 ? '#f5f9fd' : '#fff' }}>
                        <td style={td}>{i.id}</td>
                        <td style={{ ...td, color: '#1a6ea8' }}>{br(i.data)}</td>
                        <td style={td}>{i.hora.slice(0, 5)}</td>
                        <td style={td}>{i.placa}</td>
                        <td style={{ ...td, fontWeight: 700, color: '#1a6ea8' }}>{i.veicod ?? '—'}</td>
                        <td style={td}>{i.combustivel} {i.letra && `(${i.letra})`}</td>
                        <td style={dir}>{fmtN(i.litros, 3)}</td>
                        <td style={dir}>{fmtN(i.odometro, 0)}</td>
                        <td style={{ ...td, color: '#b91c1c' }}>{i.deposito ?? '—'}</td>
                        <td style={dir}>{fmtN(i.valorPrevisto, 2)}</td>
                        <td style={td}><span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, background: s.bg, color: s.fg }}>{s.t}</span>{i.gravado && <b style={{ color: '#15803d', marginLeft: 6 }}>✓ gravado{i.ordnum ? ` (OS ${i.ordnum})` : ''}</b>}</td>
                        <td style={{ ...td, whiteSpace: 'normal', minWidth: 260 }}>
                          {i.erp && (
                            <span style={{ color: i.confere ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
                              {i.confere ? '✓ confere' : '✗ diferente'} — {i.erp.ordnum ? `OS ${i.erp.ordnum} • ` : ''}{i.erp.deposito} • {i.erp.letra} • {fmtN(i.erp.litros, 3)} L • hod {fmtN(i.erp.odometro, 0)}{i.erp.ordnum ? '' : ` • R$ ${fmtN(i.erp.valor, 2)}`}
                            </span>
                          )}
                          {i.erros.length > 0 && <span style={{ color: '#b91c1c' }}>{i.erros.join(' • ')}</span>}
                          {i.incorreto && <b style={{ color: '#b45309', marginLeft: 6 }}>→ enviado pra correção (nº {i.incorreto})</b>}
                        </td>
                      </tr>
                    )
                  })}
                  {itens.length === 0 && <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum abastecimento retornado pelo CTA a partir desse ID.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
